import React, { useEffect, useRef, useState } from "react";
import { cameraService } from "client-app-core";
import { useEnsurePTZTempLock } from "../../../SharedComponents/CameraPTZ/ensurePTZTempLock";

const VideoIntegratedControls = ({ instanceId, camera, customStyles }) => {
	const cameraId = camera.id;
	const ensureTempLock = useEnsurePTZTempLock(cameraId);

	const instanceIdState = useRef(instanceId);
	const ctrlEngaged = useRef(false);
	const controlSurface = useRef(null);
	const lastSentRef = useRef(0);
	const pendingCommandRef = useRef(null);

	const [line, setLine] = useState(null); // {x1, y1, x2, y2}

	useEffect(() => {
		const el = controlSurface.current;
		if (!el) return;

		const handler = e => {
			let zoomFactor = camera.entityData.properties.vicZoomFactor || 20;
			const zoomDirFactor = zoomFactor < 0 ? 1 : -1;
			zoomFactor = Math.abs(zoomFactor);
			const distance = Math.abs(Math.round(e.deltaY / 100) / zoomFactor);
			if (distance !== 0) {
				const direction = (e.deltaY < 0 ? -1 : 1) * zoomDirFactor;
				relativeZoom(direction, distance, 1);
			}

			e.preventDefault();
			e.stopPropagation();
		};

		const opts = { passive: false, capture: true };

		el.addEventListener("wheel", handler, opts);
		el.addEventListener("mousewheel", handler, opts);

		return () => {
			el.removeEventListener("wheel", handler, opts);
			el.removeEventListener("mousewheel", handler, opts);
		};
	}, []);

	const _onPointerDown = e => {
		controlSurface.current.setPointerCapture(e.nativeEvent.pointerId);
		const { offsetX, offsetY } = e.nativeEvent;
		controlSurface.current.startX = offsetX;
		controlSurface.current.startY = offsetY;
		controlSurface.current.lastX = 0;
		controlSurface.current.lastY = 0;
		ctrlEngaged.current = true;
		setLine({ x1: offsetX, y1: offsetY, x2: offsetX, y2: offsetY });
	};

	const _onPointerUp = e => {
		ctrlEngaged.current = false;
		controlSurface.current.releasePointerCapture(e.nativeEvent.pointerId);
		stopContinuousPanTilt();
		setLine(null);
	};

	const _onPointerMove = e => {
		e.stopPropagation();
		e.preventDefault();
		if (ctrlEngaged.current) {
			const { offsetX, offsetY } = e.nativeEvent;
			moveContinuousPanTilt(offsetX, offsetY);
			setLine(prev => (prev ? { ...prev, x2: offsetX, y2: offsetY } : prev));
		}
	};

	const moveContinuousPanTilt = (x, y) => {
		ensureTempLock();

		let panFactor = camera.entityData.properties.vicPanFactor || 10;
		let tiltFactor = camera.entityData.properties.vicTiltFactor || 10;

		const deltaX = x - controlSurface.current.startX;
		const deltaY = controlSurface.current.startY - y;

		const panDir = (deltaX < 0 ? -1 : 1) * (panFactor < 0 ? -1 : 1);
		panFactor = Math.abs(panFactor);
		let panSpd = Math.round(Math.abs(deltaX) / 10) / panFactor;
		panSpd = panSpd > 1 ? 1 : panSpd;

		const tiltDir = (deltaY < 0 ? 1 : -1) * (tiltFactor < 0 ? -1 : 1);
		tiltFactor = Math.abs(tiltFactor);
		let tiltSpd = Math.round(Math.abs(deltaY) / 10) / tiltFactor;
		tiltSpd = tiltSpd > 1 ? 1 : tiltSpd;

		const panValue = Number((panSpd * panDir).toFixed(2));
		const tiltValue = Number((tiltSpd * tiltDir).toFixed(2));

		const now = Date.now();

		// Only send if changed
		if (controlSurface.current.lastX !== panValue || controlSurface.current.lastY !== tiltValue) {
			if (now - lastSentRef.current >= 200) {
				cameraService.moveContinuous(camera.id, instanceIdState.current, panValue, tiltValue, 0);
				controlSurface.current.lastX = panValue;
				controlSurface.current.lastY = tiltValue;
				lastSentRef.current = now;
				pendingCommandRef.current = null;
			} else {
				// Save the pending command to send later
				pendingCommandRef.current = { panValue, tiltValue };
			}
		}
	};

	// Ensure last change is always sent once, even if removed by throttle
	useEffect(() => {
		const interval = setInterval(() => {
			if (pendingCommandRef.current) {
				const now = Date.now();
				if (now - lastSentRef.current >= 200) {
					// <-- respect throttle here
					cameraService.moveContinuous(
						camera.id,
						instanceIdState.current,
						pendingCommandRef.current.panValue,
						pendingCommandRef.current.tiltValue,
						0
					);
					controlSurface.current.lastX = pendingCommandRef.current.panValue;
					controlSurface.current.lastY = pendingCommandRef.current.tiltValue;
					lastSentRef.current = now;
					pendingCommandRef.current = null;
				}
			}
		}, 50);
		return () => clearInterval(interval);
	}, [camera.id]);

	const stopContinuousPanTilt = async () => {
		ensureTempLock();

		cameraService.moveContinuous(camera.id, instanceIdState.current, 0, 0, 0);
		controlSurface.current.lastX = 0;
		controlSurface.current.lastY = 0;
		pendingCommandRef.current = null; // <-- Cancel any pending movement command
		// failsafe
		setTimeout(() => {
			cameraService.moveContinuous(camera.id, instanceIdState.current, 0, 0, 0);
		}, 500);
	};

	const relativeZoom = (direction, distance, speed = 1) => {
		ensureTempLock();

		cameraService.zoomRelative(camera.id, instanceIdState.current, direction, distance, speed);
	};

	const styles = {
		control_surface: {
			...customStyles,
			position: "absolute",
			zIndex: "1900",
			width: "100%",
			height: "100%"
		},
		line: {
			position: "absolute",
			pointerEvents: "none",
			left: 0,
			top: 0,
			width: "100%",
			height: "100%",
			zIndex: "2000"
		}
	};

	// Compute points for an arrowhead triangle at (x2,y2) pointing from (x1,y1) -> (x2,y2)
	const getArrowPoints = (x1, y1, x2, y2, length = 20, width = 15) => {
		const dx = x2 - x1;
		const dy = y2 - y1;
		const mag = Math.hypot(dx, dy);
		if (!mag) return null;

		const ux = dx / mag;
		const uy = dy / mag;

		// Base center of the arrowhead
		const bx = x2 - length * ux;
		const by = y2 - length * uy;

		// Perpendicular unit vector
		const px = -uy;
		const py = ux;

		const halfW = width / 2;

		// Triangle points: tip and two base corners
		const p1x = bx + halfW * px;
		const p1y = by + halfW * py;
		const p2x = bx - halfW * px;
		const p2y = by - halfW * py;

		return `${x2},${y2} ${p1x},${p1y} ${p2x},${p2y}`;
	};

	return (
		<div
			ref={controlSurface}
			style={styles.control_surface}
			onPointerDown={_onPointerDown}
			onPointerMove={_onPointerMove}
			onPointerUp={_onPointerUp} data-testid="orion-components-profiles-widgets-ptzcontrols-video-integrated-controls-div"
		>
			{line && (
				<svg style={styles.line}>
					{(() => {
						const ARROW_LEN = 20;
						const ARROW_W = 15;

						const { x1, y1, x2, y2 } = line;
						const dx = x2 - x1;
						const dy = y2 - y1;
						const mag = Math.hypot(dx, dy);
						if (!mag) return null;

						// Base of the arrowhead so the line ends at the arrow base, not under the tip
						const ux = dx / mag;
						const uy = dy / mag;
						const baseX = x2 - ARROW_LEN * ux;
						const baseY = y2 - ARROW_LEN * uy;

						const points = getArrowPoints(x1, y1, x2, y2, ARROW_LEN, ARROW_W);

						return (
							<>
								{mag >= ARROW_LEN && (
									<line
										x1={x1}
										y1={y1}
										x2={baseX}
										y2={baseY}
										stroke="white"
										strokeWidth={5}
										opacity={0.5}
									/>
								)}
								{points ? <polygon points={points} fill="white" opacity={0.5} /> : null}
							</>
						);
					})()}
				</svg>
			)}
		</div>
	);
};

export default VideoIntegratedControls;
